import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, map, catchError, throwError, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ToastService } from '../toast.service';

export interface AuthUser {
  id: string;
  name: string;
  username: string;
  email: string;
  role: string;
  departmentId?: string;
  avatarUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly API_URL = environment.apiUrl;
  public readonly APP_NAMESPACE = 'savinda_tms';
  private readonly toastService = inject(ToastService);

  readonly currentUser = signal<AuthUser | null>(this.getStoredUser());
  readonly isAuthenticated = signal<boolean>(this.hasValidSession());

  constructor(private readonly http: HttpClient, private readonly router: Router) {
    if (this.hasValidSession()) {
      queueMicrotask(() => {
        this.fetchProfile().subscribe();
      });
    }
  }

  login(authKey: string): Observable<boolean> {
    return this.http
      .post<any>(`${this.API_URL}/auth/exchange`, { authKey })
      .pipe(
        map((res) => {
          const body = res?.data || res;
          const accessToken = body?.accessToken || body?.access_token;
          const refreshToken = body?.refreshToken || body?.refresh_token;
          const rawUser = body?.user || body;
          const roles = body?.roles || rawUser?.roles || [];
          const user = this.normalizeUser(rawUser, roles);
          return { accessToken, refreshToken, user };
        }),
        tap(({ accessToken, refreshToken, user }) => {
          if (accessToken) {
            this.storeSession(accessToken, refreshToken, user);
          }
        }),
        map(({ accessToken }) => !!accessToken),
      );
  }

  loginGuest(): Observable<boolean> {
    return this.http
      .post<any>(`${this.API_URL}/auth/guest`, {})
      .pipe(
        map((res) => {
          const body = res?.data || res;
          const accessToken = body?.accessToken || body?.access_token;
          const refreshToken = body?.refreshToken || body?.refresh_token;
          const rawUser = body?.user || body;
          const roles = body?.roles || rawUser?.roles || [];
          const user = this.normalizeUser(rawUser, roles);
          return { accessToken, refreshToken, user };
        }),
        tap(({ accessToken, refreshToken, user }) => {
          if (accessToken) {
            this.storeSession(accessToken, refreshToken, user);
            this.toastService.info(
              'Guest Mode Active',
              'You have entered the system as a Guest User.'
            );
          }
        }),
        map(({ accessToken }) => !!accessToken),
        catchError(() => {
          return this.http.post<any>(`${this.API_URL}/auth/exchange`, {}).pipe(
            map((res) => {
              const body = res?.data || res;
              const accessToken = body?.accessToken || body?.access_token;
              const refreshToken = body?.refreshToken || body?.refresh_token;
              const rawUser = body?.user || body;
              const roles = body?.roles || rawUser?.roles || [];
              const user = this.normalizeUser(rawUser, roles);
              return { accessToken, refreshToken, user };
            }),
            tap(({ accessToken, refreshToken, user }) => {
              if (accessToken) {
                this.storeSession(accessToken, refreshToken, user);
                this.toastService.info(
                  'Guest Mode Active',
                  'You have entered the system as a Guest User.'
                );
              }
            }),
            map(({ accessToken }) => !!accessToken),
            catchError(() => of(false)),
          );
        }),
      );
  }

  directEmailLogin(email: string): Observable<boolean> {
    return this.http
      .get<any>(`${this.API_URL}/auth/direct-email-login`, { params: { email } })
      .pipe(
        map((res) => {
          const body = res?.data || res;
          const accessToken = body?.accessToken || body?.access_token;
          const refreshToken = body?.refreshToken || body?.refresh_token;
          const rawUser = body?.user || body;
          const roles = body?.roles || rawUser?.roles || [];
          const user = this.normalizeUser(rawUser, roles);
          return { accessToken, refreshToken, user };
        }),
        tap(({ accessToken, refreshToken, user }) => {
          if (accessToken) {
            this.storeSession(accessToken, refreshToken, user);
          }
        }),
        map(({ accessToken }) => !!accessToken),
        catchError(() => of(false)),
      );
  }

  refreshToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<any>(`${this.API_URL}/auth/refresh`, { refreshToken }).pipe(
      map((res) => {
        const body = res?.data || res;
        return body?.accessToken || body?.access_token;
      }),
      tap((accessToken) => {
        if (accessToken) {
          sessionStorage.setItem(`${this.APP_NAMESPACE}_access_token`, accessToken);
          localStorage.setItem(`${this.APP_NAMESPACE}_access_token`, accessToken);
        }
      }),
    );
  }

  fetchProfile(): Observable<AuthUser | null> {
    return this.http.get<any>(`${this.API_URL}/auth/profile`).pipe(
      map((res) => (res.data ? res.data : res)),
      tap((profile) => {
        const rawUser = profile.user || profile;
        const roles: string[] = profile.roles || rawUser.roles || [];
        const user = this.normalizeUser(rawUser, roles);
        this.currentUser.set(user);
        sessionStorage.setItem(`${this.APP_NAMESPACE}_user`, JSON.stringify(user));
        localStorage.setItem(`${this.APP_NAMESPACE}_user`, JSON.stringify(user));
      }),
      catchError(() => of(null)),
    );
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      this.http.post(`${this.API_URL}/auth/logout`, { refreshToken }).subscribe();
    }
    this.clearSession();
    window.location.href = environment.PORTAL;
  }

  getAccessToken(): string | null {
    const token =
      sessionStorage.getItem(`${this.APP_NAMESPACE}_access_token`) ||
      localStorage.getItem(`${this.APP_NAMESPACE}_access_token`);

    if (!token || token === 'undefined' || token === 'null' || token.trim() === '') {
      return null;
    }
    return token;
  }

  getRefreshToken(): string | null {
    const token =
      sessionStorage.getItem(`${this.APP_NAMESPACE}_refresh_token`) ||
      localStorage.getItem(`${this.APP_NAMESPACE}_refresh_token`);

    if (!token || token === 'undefined' || token === 'null' || token.trim() === '') {
      return null;
    }
    return token;
  }

  getUsername(): string {
    return this.currentUser()?.name || this.currentUser()?.username || 'Enterprise Admin';
  }

  homePage(): void {
    this.router.navigate(['/dashboard']);
  }

  private normalizeUser(rawUser: any, rolesInput?: string[]): AuthUser {
    if (!rawUser) return rawUser;

    const roles: string[] = rolesInput && rolesInput.length > 0
      ? rolesInput
      : (Array.isArray(rawUser.roles) ? rawUser.roles : []);

    let role = rawUser.role;
    if (!role || role === 'System Admin' || role === 'SYSTEM_ADMIN') {
      if (roles.includes('SYSTEM_ADMIN')) role = 'System Admin';
      else if (roles.includes('ADMIN')) role = 'Administrator';
      else if (roles.includes('DEPARTMENT_HEAD')) role = 'Department Head';
      else if (roles.includes('IT_SUPPORT')) role = 'IT Support';
      else if (roles.includes('EMPLOYEE')) role = 'Employee';
      else if (roles.length > 0) role = this.formatRoleName(roles[0]);
      else role = role ? this.formatRoleName(role) : 'Employee';
    } else {
      role = this.formatRoleName(role);
    }

    return {
      ...rawUser,
      role,
      roles,
    };
  }

  private formatRoleName(roleCode: string): string {
    if (!roleCode) return 'Employee';
    switch (roleCode.toUpperCase()) {
      case 'SYSTEM_ADMIN': return 'System Admin';
      case 'ADMIN': return 'Administrator';
      case 'DEPARTMENT_HEAD': return 'Department Head';
      case 'IT_SUPPORT': return 'IT Support';
      case 'EMPLOYEE': return 'Employee';
      default: return roleCode;
    }
  }

  private storeSession(accessToken: string, refreshToken: string, user: AuthUser): void {
    if (!accessToken) return;

    sessionStorage.setItem(`${this.APP_NAMESPACE}_access_token`, accessToken);
    localStorage.setItem(`${this.APP_NAMESPACE}_access_token`, accessToken);

    if (refreshToken) {
      sessionStorage.setItem(`${this.APP_NAMESPACE}_refresh_token`, refreshToken);
      localStorage.setItem(`${this.APP_NAMESPACE}_refresh_token`, refreshToken);
    }
    if (user) {
      const normalized = this.normalizeUser(user);
      sessionStorage.setItem(`${this.APP_NAMESPACE}_user`, JSON.stringify(normalized));
      localStorage.setItem(`${this.APP_NAMESPACE}_user`, JSON.stringify(normalized));
      this.currentUser.set(normalized);
    }

    this.isAuthenticated.set(true);
  }

  private clearSession(): void {
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_access_token`);
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_refresh_token`);
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_user`);
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_ticket_filters`);
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_last_workspace_route`);

    localStorage.removeItem(`${this.APP_NAMESPACE}_access_token`);
    localStorage.removeItem(`${this.APP_NAMESPACE}_refresh_token`);
    localStorage.removeItem(`${this.APP_NAMESPACE}_user`);

    this.currentUser.set(null);
    this.isAuthenticated.set(false);
  }

  private hasValidSession(): boolean {
    return !!this.getAccessToken();
  }

  private getStoredUser(): AuthUser | null {
    const raw =
      sessionStorage.getItem(`${this.APP_NAMESPACE}_user`) ||
      localStorage.getItem(`${this.APP_NAMESPACE}_user`);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return this.normalizeUser(parsed);
    } catch {
      return null;
    }
  }
}
